// src/app/modules/apps/ticket/components/ticket-list/core/QueryResponseProvider.tsx

/* eslint-disable react-hooks/exhaustive-deps */
import {FC, useContext, useState, useEffect, useMemo} from 'react'
import {useQuery} from 'react-query'
import {
  createResponseContext,
  initialQueryResponse,
  initialQueryState,
  PaginationState,
  QUERIES,
  stringifyRequestQuery,
  WithChildren,
} from '../../../../../../../_metronic/helpers'
import {getData} from './_requests'
import {Data} from './_models'
import {useQueryRequest} from './QueryRequestProvider'

const QueryResponseContext = createResponseContext<Data>(initialQueryResponse)

const QueryResponseProvider: FC<WithChildren> = ({children}) => {
  const {state} = useQueryRequest()
  const [query, setQuery] = useState<string>(stringifyRequestQuery(state))
  const updatedQuery = useMemo(() => stringifyRequestQuery(state), [state])

  useEffect(() => {
    if (query !== updatedQuery) {
      setQuery(updatedQuery)
    }
  }, [updatedQuery])

  const {
    isFetching,
    refetch,
    data: response,
  } = useQuery(
    [QUERIES.TICKET_LIST, query],
    () => {
      return getData(query)
    },
    {cacheTime: 0, keepPreviousData: true, refetchOnWindowFocus: false}
  )

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!response || !response.data) {
      return []
    }

    // If there's no search term, return original data
    if (!state.search || state.search.trim() === '') {
      return response.data
    }

    const searchTerm = state.search.toLowerCase().trim()
    
    // Filter data based on search term across all fields
    return response.data.filter((item: Data) => {
      return Object.values(item).some((value) => {
        if (value === null || value === undefined) {
          return false
        }
        return value.toString().toLowerCase().includes(searchTerm)
      })
    })
  }, [response, state.search])

  // Format status with proper capitalization
  const formattedData = useMemo(() => {
    return filteredData.map((item) => ({
      ...item,
      status: item.status
        ? item.status.charAt(0).toUpperCase() + item.status.slice(1)
        : item.status
    }))
  }, [filteredData])

  return (
    <QueryResponseContext.Provider value={{isLoading: isFetching, refetch, response, query}}>
      {children}
    </QueryResponseContext.Provider>
  )
}

const useQueryResponse = () => useContext(QueryResponseContext)

const useQueryResponseData = () => {
  const {response} = useQueryResponse()
  
  if (!response || !response.data) {
    return []
  }

  return response.data.map((item) => ({
    ...item,
    status: item.status
      ? item.status.charAt(0).toUpperCase() + item.status.slice(1)
      : item.status
  }))
}

const useQueryResponsePagination = () => {
  const defaultPaginationState: PaginationState = {
    links: [],
    ...initialQueryState,
  }

  const {response} = useQueryResponse()
  if (!response || !response.payload || !response.payload.pagination) {
    return defaultPaginationState
  }

  return response.payload.pagination
}

const useQueryResponseLoading = (): boolean => {
  const {isLoading} = useQueryResponse()
  return isLoading
}

export {
  QueryResponseProvider,
  useQueryResponse,
  useQueryResponseData,
  useQueryResponsePagination,
  useQueryResponseLoading,
}